import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  ImageList,
  ImageListItem,
  TextField,
  Typography,
} from "@mui/material";
import { FC, FormEvent, useState } from "react";

interface WikiImage {
  title: string;
  url: string;
}

interface WikiImageSearchProps {
  open: boolean;
  onClose: () => void;
  onSelect: (url: string) => void;
}

const WIKI_API = "https://en.wikipedia.org/w/api.php";

const searchWikiImages = async (query: string): Promise<WikiImage[]> => {
  const params = new URLSearchParams({
    action: "query",
    generator: "search",
    gsrsearch: query,
    gsrlimit: "20",
    prop: "pageimages",
    piprop: "thumbnail",
    pithumbsize: "300",
    format: "json",
    origin: "*",
  });

  const res = await fetch(`${WIKI_API}?${params}`);
  const data = await res.json();

  if (!data.query?.pages) return [];

  return Object.values(data.query.pages)
    .filter((page: any) => page.thumbnail?.source)
    .map((page: any) => ({
      title: page.title,
      url: page.thumbnail.source,
    }));
};

const getFullImageUrl = (thumbnailUrl: string): string => {
  // Convert thumbnail URL to full-size image URL
  // Thumbnail: .../thumb/X/XX/File.png/300px-File.png
  // Full: .../X/XX/File.png
  return thumbnailUrl.replace(/\/thumb\//, "/").replace(/\/\d+px-[^/]+$/, "");
};

export const WikiImageSearch: FC<WikiImageSearchProps> = ({
  open,
  onClose,
  onSelect,
}) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<WikiImage[]>([]);
  const [loading, setLoading] = useState(false);

  const handleSearch = async (e: FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    try {
      const images = await searchWikiImages(query);
      setResults(images);
    } catch {
      setResults([]);
    }
    setLoading(false);
  };

  const handleSelect = (img: WikiImage) => {
    onSelect(getFullImageUrl(img.url));
    onClose();
    setQuery("");
    setResults([]);
  };

  const handleClose = () => {
    onClose();
    setQuery("");
    setResults([]);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Buscar imagen en Wikipedia</DialogTitle>
      <DialogContent>
        <form onSubmit={handleSearch}>
          <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
            <TextField
              autoFocus
              fullWidth
              placeholder="Ej: Millonarios FC, Independiente Santa Fe..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              size="small"
            />
            <Button type="submit" variant="contained" disabled={loading}>
              Buscar
            </Button>
          </Box>
        </form>

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && results.length === 0 && query && (
          <Typography color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
            No se encontraron imágenes
          </Typography>
        )}

        {results.length > 0 && (
          <ImageList cols={3} gap={8} sx={{ maxHeight: 400 }}>
            {results.map((img) => (
              <ImageListItem
                key={img.title}
                onClick={() => handleSelect(img)}
                sx={{
                  cursor: "pointer",
                  border: "2px solid transparent",
                  borderRadius: 1,
                  overflow: "hidden",
                  "&:hover": { border: "2px solid #1976d2" },
                }}
              >
                <img
                  src={img.url}
                  alt={img.title}
                  loading="lazy"
                  style={{ height: 150, objectFit: "contain", background: "#f5f5f5" }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    textAlign: "center",
                    p: 0.5,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {img.title}
                </Typography>
              </ImageListItem>
            ))}
          </ImageList>
        )}
      </DialogContent>
    </Dialog>
  );
};
